import { Button, Divider } from "antd";
import { filterAtom } from "components/UI/Table/atoms/filterAtom.ts";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { useTable } from "components/UI/Table/hooks/useTable.ts";
import { QuickFilterOption } from "components/UI/Table/types/table.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { SearchFilterEnum } from "types/enums";
import { NonIdentifiable, TableId } from "types/models.ts";

interface QuickFilterPillsProps {
    tableId: TableId;
    dataIndex: string;
    options: QuickFilterOption[];
    operator?: SearchFilterEnum;
}

export const QuickFilterPills = ({
    tableId,
    dataIndex,
    options,
    operator = SearchFilterEnum.Equal,
}: QuickFilterPillsProps) => {
    const { t: tC } = usePaldockTranslation("common");
    const tableUtils = useTable(tableId);
    const tableState = useRecoilValue(tableAtom<NonIdentifiable>()(tableId));
    const [, setFilters] = useRecoilState(filterAtom(tableId));
    const rawValue = tableState.filter?.filter?.[dataIndex]?.[operator];
    const selectedValue = typeof rawValue === "string" ? rawValue : undefined;

    // URL filters can initialize before asynchronous options arrive. Refresh the filter
    // rule's labels so the expanded column filter renders the same option as the pills.
    useEffect(() => {
        setFilters((currentFilters) => {
            const matchingFilter = currentFilters.find(
                (filter) => (filter.filterName ?? filter.dataIndex) === dataIndex && filter.operator === operator
            );
            if (!matchingFilter || matchingFilter.filterOptions === options) return currentFilters;

            return currentFilters.map((filter) =>
                filter === matchingFilter ? { ...filter, filterOptions: options } : filter
            );
        });
    }, [dataIndex, operator, options, setFilters]);

    const saveValue = (value?: string) => {
        tableUtils.saveQuickFilter(dataIndex, operator, value, options);
    };

    return (
        <div className="flex items-center gap-2 overflow-x-auto" role="group" aria-label={tC("filter")}>
            <Button
                className="!rounded-md"
                type={selectedValue === undefined ? "primary" : "default"}
                aria-pressed={selectedValue === undefined}
                onClick={() => saveValue()}
            >
                {tC("all")}
            </Button>
            <Divider type="vertical" className="mx-0 h-6" />
            {options.map((option) => (
                <Button
                    key={option.value}
                    className="!rounded-md"
                    type={selectedValue === option.value ? "primary" : "default"}
                    aria-pressed={selectedValue === option.value}
                    onClick={() => saveValue(option.value)}
                >
                    {option.label}
                </Button>
            ))}
        </div>
    );
};
